import { Component, ElementRef, Input, OnInit, ChangeDetectionStrategy, inject, input, viewChild } from '@angular/core';
import { MatDialogRef, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { PermissionObject } from '@eadsysteme/ewsauthclient-v1';
import { ConfigService } from '../../services/config/config.service';
import { Permission } from '../../services/data/permission';
import { SnackbarService } from '../../services/snackbar.service';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { AraQrGeneratorComponent } from '../util/ara-qr-generator/ara-qr-generator.component';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import PermissionTypeEnum = PermissionObject.PermissionTypeEnum;

@Component({
	selector: 'ara-image-dialog',
	templateUrl: './image-dialog.component.html',
	styleUrls: ['./image-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [TranslocoDirective, CdkScrollable, MatDialogContent, AraQrGeneratorComponent, MatDialogActions, MatButton, MatIcon],
})
export class ImageDialogComponent implements OnInit {
	dialogRef = inject<MatDialogRef<ImageDialogComponent>>(MatDialogRef);
	private configService = inject(ConfigService);
	private snackBar = inject(SnackbarService);
	private translocoService = inject(TranslocoService);

	readonly qrCodeContainer = viewChild<ElementRef<HTMLElement>>('qrCodeContainer');
	@Input()
	permission: Permission;
	@Input()
	type: string;
	public readonly width = input('auto');
	public readonly height = input('auto');
	public imageSrc = '';
	public qrCodeValue: string;

	ngOnInit(): void {
		if (this.permission?.permissionType === PermissionTypeEnum.RESIDENT) {
			this.qrCodeValue = this.configService.getAraAppUrl();
		}
	}

	async copyQrCodeImage(): Promise<void> {
		try {
			if (!navigator?.clipboard || typeof ClipboardItem === 'undefined') {
				throw new Error();
			}
			const qrCodeContainer = this.qrCodeContainer()?.nativeElement;
			if (!qrCodeContainer) {
				throw new Error();
			}
			const qrBlob = await this.getQrBlob(qrCodeContainer);
			if (!qrBlob) {
				throw new Error();
			}
			const clipboardType = qrBlob.type || 'image/png';
			await navigator.clipboard.write([new ClipboardItem({ [clipboardType]: qrBlob })]);
			this.snackBar.success(this.translocoService.translate('permissions.copyQrImageSuccess'));
		} catch {
			this.snackBar.error(this.translocoService.translate('permissions.copyQrImageError'));
		}
	}

	private async getQrBlob(qrCodeContainer: HTMLElement): Promise<Blob | null> {
		const qrCanvas = qrCodeContainer.querySelector('canvas') as HTMLCanvasElement | null;
		if (qrCanvas) {
			return new Promise((resolve) => qrCanvas.toBlob((blob) => resolve(blob), 'image/png'));
		}
		const qrImage = qrCodeContainer.querySelector('img') as HTMLImageElement | null;
		if (qrImage?.src) {
			const imageResponse = await fetch(qrImage.src);
			return imageResponse.blob();
		}
		return null;
	}
}
